import {
  DependencyList,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useResizeObserver } from 'usehooks-ts';

import { isDefined } from '../lib';

export const useOverflowMask = <T extends HTMLElement>(
  deps: DependencyList = [],
) => {
  const ref = useRef<T>(null);
  const [needsFade, setNeedsFade] = useState(false);
  const frameRef = useRef<number | null>(null);

  const check = useCallback(() => {
    const el = ref.current;
    if (!el) {
      return;
    }

    const next = el.scrollWidth > el.clientWidth + 1;
    setNeedsFade(prev => (prev === next ? prev : next));
  }, []);

  const scheduleCheck = useCallback(() => {
    if (isDefined(frameRef.current)) {
      cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      check();
    });
  }, [check]);

  useResizeObserver({
    ref: ref as RefObject<HTMLElement>,
    box: 'border-box',
    onResize: scheduleCheck,
  });

  useEffect(scheduleCheck, [scheduleCheck, ...deps]);

  useEffect(
    () => () => {
      if (isDefined(frameRef.current)) {
        cancelAnimationFrame(frameRef.current);
      }
    },
    [],
  );

  return { ref, needsFade };
};
